import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Bell, BellRing, Smartphone, X, Download } from 'lucide-react';
import { Button } from './ui/button';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const DISMISS_KEY_INSTALL = 'crm_pwa_install_dismissed';
const DISMISS_KEY_PUSH = 'crm_pwa_push_dismissed';

// Converte una chiave VAPID base64url in Uint8Array (formato richiesto da PushManager.subscribe)
const urlBase64ToUint8Array = (base64String) => {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
  return arr;
};

const isStandalone = () =>
  window.matchMedia?.('(display-mode: standalone)').matches ||
  window.navigator.standalone === true;

const isIos = () => /iphone|ipad|ipod/i.test(window.navigator.userAgent);

const PwaSetup = () => {
  const { user } = useAuth();
  const [installPrompt, setInstallPrompt] = useState(null);
  const [showInstall, setShowInstall] = useState(false);
  const [showPush, setShowPush] = useState(false);
  const [pushEnabled, setPushEnabled] = useState(false);
  const [busy, setBusy] = useState(false);
  const [showIosHint, setShowIosHint] = useState(false);

  // Verifica stato notifiche push su mount
  const checkPushState = useCallback(async () => {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) return;
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      setPushEnabled(!!sub);
      if (!sub && Notification.permission !== 'denied' && !sessionStorage.getItem(DISMISS_KEY_PUSH)) {
        // Mostra il prompt dopo un piccolo delay per non essere invasivo
        setTimeout(() => setShowPush(true), 4000);
      }
    } catch (e) {
      console.warn('Push state check failed', e);
    }
  }, []);

  useEffect(() => {
    if (!user) return;
    // Prompt di installazione PWA (Chrome/Edge)
    const handler = (e) => {
      e.preventDefault();
      setInstallPrompt(e);
      if (!localStorage.getItem(DISMISS_KEY_INSTALL) && !isStandalone()) {
        setShowInstall(true);
      }
    };
    window.addEventListener('beforeinstallprompt', handler);
    // iOS non emette beforeinstallprompt -> mostra istruzioni manuali
    if (isIos() && !isStandalone() && !localStorage.getItem(DISMISS_KEY_INSTALL)) {
      setTimeout(() => setShowIosHint(true), 2500);
    }
    checkPushState();
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, [user, checkPushState]);

  const handleInstall = async () => {
    if (!installPrompt) return;
    installPrompt.prompt();
    const result = await installPrompt.userChoice;
    if (result.outcome === 'accepted') {
      toast.success('App installata! Trovi Schiliro CRM sulla home del telefono.');
    }
    setInstallPrompt(null);
    setShowInstall(false);
  };

  const dismissInstall = () => {
    localStorage.setItem(DISMISS_KEY_INSTALL, 'true');
    setShowInstall(false);
    setShowIosHint(false);
  };

  const enablePush = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const perm = await Notification.requestPermission();
      if (perm !== 'granted') {
        toast.error('Permesso notifiche negato. Puoi attivarlo dalle impostazioni del browser.');
        setShowPush(false);
        return;
      }
      const reg = await navigator.serviceWorker.ready;
      // Ottieni chiave pubblica VAPID dal backend
      const keyRes = await axios.get(`${API_URL}/api/push/vapid-public-key`);
      const pub = keyRes.data.public_key;
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(pub),
      });
      const json = sub.toJSON();
      await axios.post(`${API_URL}/api/push/subscribe`, {
        endpoint: json.endpoint,
        keys: { p256dh: json.keys.p256dh, auth: json.keys.auth },
      });
      setPushEnabled(true);
      setShowPush(false);
      toast.success('Notifiche push attivate! Riceverai gli avvisi critici anche a schermo spento.');
    } catch (e) {
      console.warn('Push subscribe error', e);
      toast.error(e.response?.data?.detail || 'Impossibile attivare le notifiche push');
    } finally {
      setBusy(false);
    }
  };

  const dismissPush = () => {
    sessionStorage.setItem(DISMISS_KEY_PUSH, 'true');
    setShowPush(false);
  };

  if (!user) return null;

  return (
    <>
      {/* Install App banner (Chrome/Edge) */}
      {showInstall && installPrompt && (
        <div
          className="fixed bottom-24 sm:bottom-6 left-4 right-4 sm:left-auto sm:right-6 sm:max-w-sm z-40 rounded-2xl bg-gradient-to-br from-sky-600 to-sky-700 text-white shadow-2xl border border-sky-400/40 p-4"
          data-testid="pwa-install-banner"
        >
          <button
            onClick={dismissInstall}
            className="absolute top-2 right-2 p-1 rounded-lg hover:bg-white/10"
            aria-label="Chiudi"
            data-testid="dismiss-install-btn"
          >
            <X className="w-4 h-4" />
          </button>
          <div className="flex items-start gap-3">
            <Smartphone className="w-6 h-6 mt-0.5 shrink-0" />
            <div className="min-w-0 flex-1">
              <p className="font-semibold text-sm">Installa Schiliro CRM</p>
              <p className="text-xs text-sky-100 mt-0.5">Icona sulla home, apre a schermo intero come app nativa.</p>
              <Button
                onClick={handleInstall}
                size="sm"
                className="mt-3 w-full bg-white text-sky-700 hover:bg-sky-50"
                data-testid="install-app-btn"
              >
                <Download className="w-4 h-4 mr-1.5" /> Installa
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Istruzioni iOS Safari (fallback) */}
      {showIosHint && (
        <div
          className="fixed bottom-6 left-4 right-4 sm:left-auto sm:right-6 sm:max-w-sm z-40 rounded-2xl bg-slate-900 text-white shadow-2xl p-4 border border-slate-700"
          data-testid="ios-install-hint"
        >
          <button
            onClick={dismissInstall}
            className="absolute top-2 right-2 p-1 rounded-lg hover:bg-white/10"
            aria-label="Chiudi"
          >
            <X className="w-4 h-4" />
          </button>
          <div className="flex items-start gap-3">
            <Smartphone className="w-6 h-6 mt-0.5 shrink-0 text-sky-400" />
            <div className="text-xs leading-relaxed">
              <p className="font-semibold text-sm mb-1">Installa su iPhone / iPad</p>
              <p className="text-slate-300">
                Tocca il pulsante <b>Condividi</b> in basso al centro di Safari, poi scegli <b>"Aggiungi a Home"</b>.
                L'app apparira' sul telefono come un'app nativa.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Push notifications enable banner */}
      {showPush && !pushEnabled && (
        <div
          className="fixed bottom-6 left-4 right-4 sm:left-auto sm:right-6 sm:max-w-sm z-40 rounded-2xl bg-white dark:bg-slate-900 shadow-2xl p-4 border border-slate-200 dark:border-slate-700"
          data-testid="push-enable-banner"
        >
          <button
            onClick={dismissPush}
            className="absolute top-2 right-2 p-1 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800"
            aria-label="Chiudi"
            data-testid="dismiss-push-btn"
          >
            <X className="w-4 h-4 text-slate-500" />
          </button>
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-fuchsia-500 to-indigo-600 flex items-center justify-center shrink-0">
              <BellRing className="w-5 h-5 text-white" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="font-semibold text-sm text-slate-900 dark:text-white">Attiva notifiche push</p>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                Ricevi al volo: nuove pratiche, cambio stato, note interne, commissioni e documenti da caricare.
              </p>
              <div className="flex items-center gap-2 mt-3">
                <Button
                  onClick={enablePush}
                  size="sm"
                  disabled={busy}
                  className="bg-indigo-600 hover:bg-indigo-700 text-white"
                  data-testid="enable-push-btn"
                >
                  <Bell className="w-4 h-4 mr-1.5" /> {busy ? 'Attivo...' : 'Attiva'}
                </Button>
                <button
                  onClick={dismissPush}
                  className="text-xs text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"
                >
                  Piu' tardi
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default PwaSetup;
